// #region Imports
import { useMediaQuery } from "@mui/material";
import { styled, Theme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import NumberBadge from "_shared/_views/badges/number-badge";
import {
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
} from "material-react-table";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetActiveWorkAgreementsCountQuery, useGetArchivedWorkAgreementsCountQuery } from "work/work-agreements/_api/work-agreement-api";
import { WorkAgreementLoadIssue } from "work/work-agreements/_store/work-agreement-redux-slice";
import { PageTab } from "work/work-agreements/_views/work-agreements-page-tabs";
import WorkAgreement from "work/work-agreements/work-agreement";

// #region Styled Components
const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
// #endregion

// #region Props
type WorkAgreementToolbarProps = {
  table: TableInstance<WorkAgreement | WorkAgreementLoadIssue>;
  activeTab: PageTab;
};

export default function WorkAgreementToolbar(props: Readonly<WorkAgreementToolbarProps>) {
  const { table, activeTab } = props;

  // #region Hooks
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const session = useSession();

  // #region Queries
  const activeWorkAgreementCountQuery = useGetActiveWorkAgreementsCountQuery({ session });
  const archivedWorkAgreementCountQuery = useGetArchivedWorkAgreementsCountQuery({ session });

  // #region Render
  return (
    <TableTopToolbarContainer>
      <TableTitle variant="h4">Work Agreements</TableTitle>
      <LowerTopToolbarContainer>
        <Tabs
          value={activeTab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_event, newValue) => navigate(`/work/${newValue}`)}
        >
          <Tab
            value={PageTab.Active}
            label={
              <NumberBadge
                isLoading={activeWorkAgreementCountQuery.isLoading}
                tooltip={`${activeWorkAgreementCountQuery.data ?? 0} active work agreement(s)`}
                badgeCount={activeWorkAgreementCountQuery.data ?? 0}
                color="info"
                text={PageTab.Active}
              />
            }
          />
          <Tab
            value={PageTab.Archived}
            label={
              <NumberBadge
                isLoading={archivedWorkAgreementCountQuery.isLoading}
                tooltip={`${archivedWorkAgreementCountQuery.data ?? 0} archived work agreement(s)`}
                badgeCount={archivedWorkAgreementCountQuery.data ?? 0}
                text={PageTab.Archived}
              />
            }
          />
        </Tabs>
      </LowerTopToolbarContainer>
      {isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <ToggleGlobalFilterButton table={table} />
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleDensePaddingButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      )}
    </TableTopToolbarContainer>
  );
}
